import { NavigationExtras } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import * as HttpStatus from 'http-status-codes';
import { List } from 'immutable';
import {
  concat,
  EMPTY,
  Observable,
  of,
  throwError as observableThrowError,
} from 'rxjs';
import {
  catchError,
  concatMap,
  delay,
  filter,
  flatMap,
  map,
  mapTo,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { v4 } from 'uuid';
import { EntityTypesEnum } from '../../app.enum';
import { AppStore, OpenGrowthDialog, openGrowthDialog } from '../../store';
import { merger, serialize } from '../../utils/helpers/model.helper';
import { AccountDataStore } from '../account-data/account-data.store';
import { Account } from '../account-data/account.model';
import { LocalStorageService } from '../browser-manager-data/local-storage.service';
import { DialogService } from '../dialog/dialog.service';
import {
  ActionsEnum as EntityActionsEnum,
  fetchFilters,
  FetchFilters,
  FetchList,
  ResetSelectedList,
  resetSelectedList,
  saveEditingEntity,
  SaveEntityStore,
  saveEntityStore,
  SearchList,
  SetDispatchedResponse,
  setDispatchedResponse,
  SetEditingEntity,
  setEditingEntity,
  SetEditingEntityContent,
  setEditingEntityContent,
  SetEditingEntityField,
  setEditingEntityField,
  UpdateEntity,
  updateEntity,
} from '../entity-data/entity-data.actions';
import { EntityDataEffects } from '../entity-data/entity-data.effects';
import { EstimateFiltersEnum } from '../estimate-data/estimate-data.data';
import {
  BannerCanvasTypesEnum,
  UPDATE_FEATURE_FRAMEWORK_DATA_DELAY,
} from '../features-data/features-data.data';
import { FeaturesDataDispatcher } from '../features-data/features-data.dispatcher';
import { FeaturesDataStore } from '../features-data/features-data.store';
import { Toggles } from '../features-data/sub-models/toggles.model';
import { ItemUnitTypesEnum } from '../item-data/item-data.data';
import { NavigationService } from '../navigation/navigation.service';
import { IPdfInfo } from '../renderer/renderer.interface';
import { RendererService } from '../renderer/renderer.service';
import * as fromSettings from '../settings-data/settings-data.actions';
import { SettingsDataStore } from '../settings-data/settings-data.store';
import { Settings } from '../settings-data/settings.model';
import { PaymentsSettings } from '../settings-data/sub-models/company-settings/payments.model';
import { GlobalBannerService } from '../shared/components/global-banner/global-banner.service';
import { UtilsService } from '../shared/services/utils/utils.service';
import {
  ActionsEnum,
  AddDocumentItemToEditing,
  BatchAddDocumentItemsToEditing,
  CopyDocument,
  DeleteDocumentItemFromEditing,
  EnableCardPayments,
  GoToDocumentAddClientScreen,
  markAsPrinted,
  MarkAsPrinted,
  MoveDocumentItemInEditing,
  PrintDocument,
  RedirectToCopiedDocument,
  redirectToCopiedDocument,
  SaveCopiedDocument,
  saveCopiedDocument,
  SendDocument,
  setInTmpCache,
  UpdateDocumentItemInEditing,
  UpdateTaxInEditing,
} from './document-data.actions';
import { DocumentDataBuilder } from './document-data.builder';
import {
  ADD_CLIENT_ACTION,
  DocumentErrorsEnum,
  DOCUMENTS_CREATE_ROUTE,
  DOCUMENTS_NEW_ROUTE,
  DocumentTypesEnum,
} from './document-data.data';
import { IDocumentState } from './document-data.reducer';
import { DocumentDataService } from './document-data.service';
import { DocumentDataStore } from './document-data.store';
import { Document } from './document.model';
import { DocumentItem } from './sub-models/document-item.model';

export abstract class DocumentDataEffects<
  T extends IDocumentState,
  U extends Document
> extends EntityDataEffects<T, U> {
  featureName: string;
  depositFeatureName: string;
  documentType: DocumentTypesEnum;

  constructor(
    protected documentDataService: DocumentDataService,
    protected documentDataStore: DocumentDataStore<T, U>,
    protected documentDataBuilder: DocumentDataBuilder<U>,
    protected accountDataStore: AccountDataStore,
    protected utilsService: UtilsService,
    protected rendererService: RendererService,
    actions: Actions,
    appStore: AppStore,
    settingsDataStore: SettingsDataStore,
    featuresDataStore: FeaturesDataStore,
    translateService: TranslateService,
    globalBannerService: GlobalBannerService,
    dialogService: DialogService,
    navigationService: NavigationService,
    localStorageService: LocalStorageService,
    featuresDataDispatcher: FeaturesDataDispatcher
  ) {
    super(
      actions,
      appStore,
      documentDataService,
      documentDataStore,
      documentDataBuilder,
      settingsDataStore,
      featuresDataStore,
      translateService,
      globalBannerService,
      dialogService,
      navigationService,
      localStorageService,
      featuresDataDispatcher
    );

    this.featureName = documentDataBuilder.featureName;
    this.depositFeatureName = documentDataBuilder.depositFeatureName;
    this.documentType = documentDataBuilder.documentType;
  }

  /**
   * Print a provided document based on browser restrictions
   * - (is printable) chrome: build a blob and print it in the browser
   * - (not printable) firefox / edge: opens a URL with the PDF
   *
   * @document: requires a full document entity, snippets will miss information
   */
  protected printDocument: Observable<
    MarkAsPrinted | SetDispatchedResponse
  > = this.actions.pipe(
    ofType(ActionsEnum.PRINT_DOCUMENT),
    filter(
      ({ payload }: PrintDocument) => payload.entityType === this.entityType
    ),
    switchMap(({ payload }: PrintDocument) => {
      const { dispatchId, document, setEditingDocument } = payload;
      const isPrintable = this.utilsService.isPrintable();

      return this.featuresDataStore.getFeatureToggles().pipe(
        withLatestFrom(this.settingsDataStore.settings$),
        flatMap(([toggles, settings]: [Toggles, Settings]) => {
          const serializedDocument = serialize(document).toJS();
          const paymentSettings: PaymentsSettings = settings.payments;
          const bankTransferEnabled: boolean =
            toggles.isAllAccess(Toggles.KEYS.STRIPE_ACH_DEBIT) &&
            paymentSettings.bankTransferEnabled() &&
            !document.settings.bankTransferDisabled;
          const cardPaymentsEnabled =
            toggles.isAllAccess(Toggles.KEYS.CARD_PAYMENTS) &&
            paymentSettings.cardPaymentsEnabled() &&
            !document.settings.cardPaymentsDisabled;
          const paypalEcEnabled =
            toggles.isAllAccess(Toggles.KEYS.PAYPAL_EC) &&
            paymentSettings.paypalEcEnabled &&
            !document.settings.paypalEcDisabled;

          return this.rendererService.renderDocumentAsPdf(
            serializedDocument,
            bankTransferEnabled,
            cardPaymentsEnabled,
            paypalEcEnabled,
            isPrintable
          );
        }),
        delay(100),
        flatMap((pdfInfo: IPdfInfo | Blob) => {
          if (isPrintable) {
            return this.utilsService
              .printRenderableEntity(pdfInfo as Blob)
              .pipe(
                catchError((error) => {
                  const [status] = error; // http error format
                  const documentError =
                    status === HttpStatus.NOT_FOUND
                      ? DocumentErrorsEnum.document_not_found
                      : DocumentErrorsEnum.print_document_error;

                  return observableThrowError(new Error(documentError));
                })
              );
          } else {
            // check if popup is blocked
            return !this.utilsService.openRenderableEntityWithUrl(
              (pdfInfo as IPdfInfo).url
            )
              ? observableThrowError(
                  new Error(DocumentErrorsEnum.print_popup_blocked)
                )
              : of(void 0); // success, fwd to markAsPrinted()
          }
        }),
        flatMap(() =>
          concat([
            // new dispatch id, we don't want to block UI from here
            markAsPrinted(v4(), this.entityType, document, setEditingDocument),
            setDispatchedResponse(
              this.entityType,
              dispatchId,
              ActionsEnum.PRINT_DOCUMENT,
              true,
              document
            ),
          ])
        ),
        catchError((error: Error) =>
          of(
            setDispatchedResponse(
              this.entityType,
              dispatchId,
              ActionsEnum.PRINT_DOCUMENT,
              false,
              document,
              error
            )
          )
        )
      );
    })
  );

  /**
   * Mark a document as printed
   */
  protected markAsPrinted: Observable<
    SaveEntityStore | SetEditingEntity
  > = this.actions.pipe(
    ofType(ActionsEnum.MARK_AS_PRINTED),
    filter(
      ({ payload }: MarkAsPrinted) => payload.entityType === this.entityType
    ),
    concatMap(({ payload }: MarkAsPrinted) => {
      const { document, setEditingDocument } = payload;
      const isNew = document.isNew();

      return this.documentDataService.markAsPrinted(document.id).pipe(
        withLatestFrom(this.settingsDataStore.settings$),
        mergeMap(([response, settings]: [any, Settings]) => {
          // [hotfix] remove client_financing_enabled
          delete response?.content?.settings?.client_financing_enabled;

          const effects = [];
          const printedDocument = this.documentDataBuilder.buildEntity(
            response,
            settings,
            false,
            isNew
          );

          effects.push(
            saveEntityStore(this.entityType, printedDocument, false)
          );

          if (setEditingDocument) {
            effects.push(setEditingEntity(this.entityType, printedDocument));
          }

          return concat(effects);
        }),
        catchError((error) =>
          this.globalBannerService.showSystemErrorBanner(error)
        )
      );
    })
  );

  protected copyDocument: Observable<
    | OpenGrowthDialog
    | SaveCopiedDocument
    | SaveEntityStore
    | RedirectToCopiedDocument
  > = this.actions.pipe(
    ofType(ActionsEnum.COPY_DOCUMENT),
    filter(
      ({ payload }: CopyDocument) => payload.entityType === this.entityType
    ),
    withLatestFrom(
      this.settingsDataStore.settings$,
      this.featuresDataStore.featureToggles$,
      this.featuresDataStore.canCreate(this.quotaName)
    ),
    switchMap(
      ([{ payload }, settings, toggles, withinQuotaLimits]: [
        CopyDocument,
        Settings,
        Toggles,
        boolean
      ]) => {
        const {
          dispatchId,
          sourceDocument,
          copyClient,
          copyItems,
          copyAttachments,
          copyTaxes,
          saveCopy,
          saveEditing,
          queryParams = null,
        } = payload;
        const effects = [];
        const sourceEntityType = EntityTypesEnum[sourceDocument.documentType];
        const sourceFeatureName = this.documentDataBuilder.featureName;
        const sourceDepositFeatureName = this.documentDataBuilder
          .depositFeatureName;
        const copyFeatureName = this.featureName;
        const copyDepositFeatureName = this.depositFeatureName;
        const isSourceAllAccess = toggles.isAllAccess(sourceFeatureName);
        const isCopyAllAccess = toggles.isAllAccess(copyFeatureName);

        if (!withinQuotaLimits) {
          effects.push(
            openGrowthDialog(
              BannerCanvasTypesEnum.quota_reached,
              this.quotaName
            )
          );
        } else if (!isSourceAllAccess) {
          effects.push(
            openGrowthDialog(
              BannerCanvasTypesEnum.feature_lock,
              sourceFeatureName
            )
          );
        } else if (!isCopyAllAccess) {
          effects.push(
            openGrowthDialog(
              BannerCanvasTypesEnum.feature_lock,
              copyFeatureName
            )
          );
        }

        // block action with growth dialog
        if (effects.length !== 0) {
          return concat([
            setDispatchedResponse(
              sourceEntityType,
              dispatchId,
              ActionsEnum.COPY_DOCUMENT,
              true,
              sourceDocument
            ),
            ...effects,
          ]);
        }

        // build deposit request copy option
        const copyDepositRequest =
          !!sourceDepositFeatureName &&
          !!copyDepositFeatureName &&
          (toggles.isAllAccess(sourceDepositFeatureName) ||
            toggles.isReadOnlyAccess(sourceDepositFeatureName)) &&
          toggles.isAllAccess(copyDepositFeatureName);

        // build copyDocumentTemplate
        const copyAsDocumentTemplate = this.entityDataBuilder.buildEntity(
          {},
          settings
        );

        // build copied document props
        const {
          updatedSourceProps,
          copiedDocumentProps,
        } = this.utilsService.copyDocument(
          sourceDocument,
          copyAsDocumentTemplate,
          copyClient,
          copyItems,
          copyAttachments,
          copyTaxes,
          copyDepositRequest
        );
        const copiedDocument: Document = this.entityDataBuilder.buildEntity(
          copiedDocumentProps
        );
        const updatedSourceDocument: Document = this.documentDataBuilder.buildEntity(
          updatedSourceProps
        );

        if (saveCopy) {
          return of(
            saveCopiedDocument(
              dispatchId,
              this.entityType,
              updatedSourceDocument,
              copiedDocument,
              saveEditing,
              queryParams
            )
          );
        }

        return of(
          redirectToCopiedDocument(
            dispatchId,
            this.entityType,
            updatedSourceDocument,
            copiedDocument,
            false,
            saveEditing,
            queryParams
          )
        );
      }
    )
  );

  protected saveCopiedDocument: Observable<
    | SaveEntityStore
    | RedirectToCopiedDocument
    | SetDispatchedResponse
    | FetchFilters
  > = this.actions.pipe(
    ofType(ActionsEnum.SAVE_COPIED_DOCUMENT),
    filter(
      ({ payload }: SaveCopiedDocument) =>
        payload.entityType === this.entityType
    ),
    withLatestFrom(this.settingsDataStore.settings$),
    concatMap(([{ payload }, settings]: [SaveCopiedDocument, Settings]) => {
      const {
        dispatchId,
        sourceDocument,
        copiedDocument,
        saveEditing,
        queryParams = null,
      } = payload;
      const sourceEntityType = EntityTypesEnum[sourceDocument.documentType];

      return this.entityDataService.saveEntity(copiedDocument).pipe(
        flatMap((document: Document) => {
          const newDocument = this.entityDataBuilder.buildEntity(
            document,
            settings,
            document.isDirty(),
            document.isNew()
          );

          setTimeout(
            () => this.featuresDataDispatcher.fetchQuotaBannersCanvases(),
            UPDATE_FEATURE_FRAMEWORK_DATA_DELAY
          );

          const effects: any[] = [
            redirectToCopiedDocument(
              dispatchId,
              this.entityType,
              sourceDocument,
              newDocument,
              true,
              saveEditing,
              queryParams
            ),
            saveEntityStore(this.entityType, newDocument, false),
          ];

          if (sourceEntityType === EntityTypesEnum.estimate) {
            effects.push(
              fetchFilters(
                EntityTypesEnum.estimate,
                this.refreshFiltersDelay,
                EstimateFiltersEnum.pending
              ),
              fetchFilters(
                EntityTypesEnum.estimate,
                this.refreshFiltersDelay,
                EstimateFiltersEnum.done
              )
            );
          }

          return concat(effects);
        }),
        catchError((error) =>
          of(
            setDispatchedResponse(
              sourceEntityType,
              dispatchId,
              ActionsEnum.COPY_DOCUMENT,
              false,
              sourceDocument,
              error
            )
          )
        )
      );
    })
  );

  protected redirectToCopiedDocument: Observable<SetDispatchedResponse> = this.actions.pipe(
    ofType(ActionsEnum.REDIRECT_TO_COPIED_DOCUMENT),
    filter(
      ({ payload }: RedirectToCopiedDocument) =>
        payload.entityType === this.entityType
    ),
    flatMap(({ payload }: RedirectToCopiedDocument) => {
      const {
        dispatchId,
        sourceDocument,
        copiedDocument,
        isSavedCopy,
        saveEditing,
        queryParams = null,
      } = payload;
      const effects = [];
      const sourceEntityType = EntityTypesEnum[sourceDocument.documentType];
      const copiedEntityType = EntityTypesEnum[copiedDocument.documentType];
      const copiedDocumentLocalId = copiedDocument.localId;
      const masterRoute = this.utilsService.getDocumentMasterRoute(
        copiedDocument.documentType
      );
      const combinedQueryParams = isSavedCopy
        ? queryParams
        : { ...queryParams, seedDataId: copiedDocumentLocalId };

      // update source document
      if (saveEditing) {
        effects.push(
          setEditingEntity(sourceEntityType, sourceDocument),
          saveEditingEntity(v4(), sourceEntityType)
        );
      } else {
        effects.push(saveEntityStore(sourceEntityType, sourceDocument, false));
      }

      if (!isSavedCopy) {
        effects.push(
          setInTmpCache(copiedEntityType, copiedDocumentLocalId, copiedDocument)
        );
      }

      effects.push(
        setDispatchedResponse(
          sourceEntityType,
          dispatchId,
          ActionsEnum.COPY_DOCUMENT,
          true,
          copiedDocument
        )
      );

      // set timeout so result can be handled on UI before redirect
      setTimeout(() => {
        const targetRoute = isSavedCopy
          ? copiedDocument.id
          : DOCUMENTS_NEW_ROUTE;

        // forces data refresh while copying in the same route
        this.navigationService
          .goToByUrl(masterRoute, { skipLocationChange: true })
          .then(() =>
            this.navigationService.goTo([masterRoute, targetRoute], {
              queryParams: combinedQueryParams,
            })
          );
      });

      return concat(effects);
    })
  );

  /**
   * Clear selected list on sorting, filter or search
   */
  protected clearSelectedEntitiesOnFetchOrSearchList: Observable<ResetSelectedList> = this.actions.pipe(
    ofType(EntityActionsEnum.FETCH_LIST, EntityActionsEnum.SEARCH_LIST),
    filter(
      ({ payload }: FetchList | SearchList) =>
        payload.entityType === this.entityType
    ),
    mapTo(resetSelectedList(this.entityType))
  );

  protected goToDocumentAddClientScreen = this.actions.pipe(
    ofType(ActionsEnum.GO_TO_DOCUMENT_ADD_CLIENT_SCREEN),
    filter(
      ({ payload }: GoToDocumentAddClientScreen) =>
        payload.entityType === this.entityType
    ),
    tap(({ payload }: GoToDocumentAddClientScreen) => {
      const { documentId, navigationExtras } = payload;
      const navExtras: NavigationExtras = {
        ...navigationExtras,
        queryParams: { action: ADD_CLIENT_ACTION },
      };

      this.navigationService.goTo(
        [
          this.utilsService.getDocumentMasterRoute(this.documentType),
          documentId,
          DOCUMENTS_CREATE_ROUTE,
        ],
        navExtras
      );
    })
  );

  protected addDocumentItemToEditing: Observable<SetEditingEntityField> = this.actions.pipe(
    ofType(ActionsEnum.ADD_DOCUMENT_ITEM_TO_EDITING),
    filter(
      ({ payload }: AddDocumentItemToEditing) =>
        payload.entityType === this.entityType
    ),
    concatMap(({ payload }: AddDocumentItemToEditing) => {
      const { documentItem } = payload;

      return this.documentDataStore.getEditingEntity().pipe(
        take(1),
        filter((x) => !!x),
        map((editingDocument: Document) => {
          const newDocumentItems = editingDocument.documentItems.push(
            documentItem
          );

          return setEditingEntityField(
            this.entityType,
            Document.CONTENT_KEYS.ITEMS,
            newDocumentItems
          );
        })
      );
    })
  );

  protected updateDocumentItemInEditing: Observable<SetEditingEntityField> = this.actions.pipe(
    ofType(ActionsEnum.UPDATE_DOCUMENT_ITEM_IN_EDITING),
    filter(
      ({ payload }: UpdateDocumentItemInEditing) =>
        payload.entityType === this.entityType
    ),
    concatMap(({ payload }: UpdateDocumentItemInEditing) => {
      const { documentItem } = payload;

      return this.documentDataStore.getEditingEntity().pipe(
        take(1),
        filter((x) => !!x),
        flatMap((editingDocument: Document) => {
          const currentDocumentItems = editingDocument.documentItems;
          const toBeUpdatedIndex = currentDocumentItems.findIndex(
            (dItem: DocumentItem) => dItem.id === documentItem.id
          );

          if (toBeUpdatedIndex > -1) {
            const newDocumentItems = currentDocumentItems.set(
              toBeUpdatedIndex,
              documentItem
            );

            return of(
              setEditingEntityField(
                this.entityType,
                Document.CONTENT_KEYS.ITEMS,
                newDocumentItems
              )
            );
          }

          return EMPTY;
        })
      );
    })
  );

  protected deleteDocumentItemFromEditing: Observable<SetEditingEntityField> = this.actions.pipe(
    ofType(ActionsEnum.DELETE_DOCUMENT_ITEM_FROM_EDITING),
    filter(
      ({ payload }: DeleteDocumentItemFromEditing) =>
        payload.entityType === this.entityType
    ),
    concatMap(({ payload }: DeleteDocumentItemFromEditing) => {
      const { documentItem } = payload;

      return this.documentDataStore.getEditingEntity().pipe(
        take(1),
        filter((x) => !!x),
        map((editingDocument: Document) => {
          const newDocumentItems = editingDocument.documentItems.filterNot(
            (dItem: DocumentItem) => dItem.id === documentItem.id
          ) as List<DocumentItem>;

          return setEditingEntityField(
            this.entityType,
            Document.CONTENT_KEYS.ITEMS,
            newDocumentItems
          );
        })
      );
    })
  );

  protected moveDocumentItemInEditing: Observable<SetEditingEntityField> = this.actions.pipe(
    ofType(ActionsEnum.MOVE_DOCUMENT_ITEM_IN_EDITING),
    filter(
      ({ payload }: MoveDocumentItemInEditing) =>
        payload.entityType === this.entityType
    ),
    concatMap(({ payload }: MoveDocumentItemInEditing) => {
      const { oldIndex, newIndex, isPart } = payload;

      return this.documentDataStore.getEditingEntity().pipe(
        take(1),
        filter((x) => !!x),
        map((editingDocument: Document) => {
          const currentDocumentItems = editingDocument.documentItems;

          if (!editingDocument.settings.separatePartsAndLabor) {
            const movingCurrentDocumentItem = currentDocumentItems.get(
              oldIndex
            );
            const newCurrentDocumentItems = currentDocumentItems
              .remove(oldIndex)
              .insert(newIndex, movingCurrentDocumentItem);

            return setEditingEntityField(
              this.entityType,
              Document.CONTENT_KEYS.ITEMS,
              newCurrentDocumentItems
            );
          }

          const groupedDocumentItems = currentDocumentItems.groupBy(
            (docItem: DocumentItem) =>
              docItem.isPart()
                ? ItemUnitTypesEnum.parts
                : ItemUnitTypesEnum.labor
          );
          const partsDocumentItems = groupedDocumentItems.get(
            ItemUnitTypesEnum.parts,
            List([])
          );
          const laborDocumentItems = groupedDocumentItems.get(
            ItemUnitTypesEnum.labor,
            List([])
          );
          const filteredGroupedDocumentItems = (isPart
            ? partsDocumentItems
            : laborDocumentItems) as List<DocumentItem>;

          const movingDocumentItem = filteredGroupedDocumentItems.get(oldIndex);
          const newFilteredGroupedDocumentItems = filteredGroupedDocumentItems
            .remove(oldIndex)
            .insert(newIndex, movingDocumentItem);

          const newDocumentItems = isPart
            ? newFilteredGroupedDocumentItems.concat(laborDocumentItems)
            : partsDocumentItems.concat(newFilteredGroupedDocumentItems);

          return setEditingEntityField(
            this.entityType,
            Document.CONTENT_KEYS.ITEMS,
            newDocumentItems as List<DocumentItem>
          );
        })
      );
    })
  );

  protected batchAddDocumentItemsToEditing: Observable<SetEditingEntityField> = this.actions.pipe(
    ofType(ActionsEnum.BATCH_ADD_DOCUMENT_ITEMS_TO_EDITING),
    filter(
      ({ payload }: BatchAddDocumentItemsToEditing) =>
        payload.entityType === this.entityType
    ),
    concatMap(({ payload }: BatchAddDocumentItemsToEditing) => {
      const { documentItems } = payload;

      return this.documentDataStore.getEditingEntity().pipe(
        take(1),
        filter((x) => !!x),
        map((editingDocument: Document) => {
          const newDocumentItems = editingDocument.documentItems.push(
            ...documentItems.toArray()
          );

          return setEditingEntityField(
            this.entityType,
            Document.CONTENT_KEYS.ITEMS,
            newDocumentItems
          );
        })
      );
    })
  );

  protected updateTaxInEditing: Observable<SetEditingEntity> = this.actions.pipe(
    ofType(ActionsEnum.UPDATE_TAX_IN_EDITING),
    filter(
      ({ payload }: UpdateTaxInEditing) =>
        payload.entityType === this.entityType
    ),
    concatMap(({ payload }: UpdateTaxInEditing) => {
      const { editTaxRateProperties } = payload;

      return this.documentDataStore.getEditingEntity().pipe(
        take(1),
        filter((x) => !!x),
        withLatestFrom(this.settingsDataStore.settings$),
        map(([editingDocument, settings]: [Document, Settings]) => {
          const updatedDocument = this.documentDataBuilder.buildUpdatedDocumentItemsWithTax(
            editingDocument,
            editTaxRateProperties.tax
          );
          const newDocumentProps = editingDocument.decorateWithKeys(
            merger(updatedDocument, editTaxRateProperties.documentProperties)
          ) as Document;
          const newDocument = this.documentDataBuilder.buildEntity(
            newDocumentProps,
            settings,
            true,
            editingDocument.isNew(),
            true
          );

          return setEditingEntity(this.entityType, newDocument);
        })
      );
    })
  );

  protected enableCardPayments: Observable<
    | SetEditingEntityContent
    | UpdateEntity
    | fromSettings.UpdateCardPaymentsSettings
  > = this.actions.pipe(
    ofType(ActionsEnum.ENABLE_CARD_PAYMENTS),
    filter(
      ({ payload }: EnableCardPayments) =>
        payload.entityType === this.entityType
    ),
    switchMap(({ payload }: EnableCardPayments) => {
      const { dispatchId, documentId, href, setEditingDocument } = payload;

      const documentCardPaymentsEnabled = this.documentDataBuilder.buildDocumentSettingsPropsForCardPayments(
        true
      );
      const effects = [
        setEditingDocument
          ? setEditingEntityContent(
              dispatchId,
              this.entityType,
              documentCardPaymentsEnabled,
              true
            )
          : updateEntity(
              dispatchId,
              this.entityType,
              documentId,
              documentCardPaymentsEnabled
            ),
        fromSettings.updateCardPaymentsSettings(dispatchId, true, href),
      ];

      return concat(effects);
    })
  );

  protected sendDocument: Observable<
    SaveEntityStore | SetDispatchedResponse
  > = this.actions.pipe(
    ofType(ActionsEnum.SEND_DOCUMENT),
    filter(
      ({ payload }: SendDocument) => payload.entityType === this.entityType
    ),
    withLatestFrom(
      this.accountDataStore.account$,
      this.featuresDataStore.featureToggles$,
      this.settingsDataStore.settings$
    ),
    switchMap(
      ([{ payload }, account, toggles, settings]: [
        SendDocument,
        Account,
        Toggles,
        Settings
      ]) => {
        const {
          dispatchId,
          document,
          bankTransferFeatureEnabled,
          cardPaymentsFeatureEnabled,
          paypalFeatureEnabled,
          messageDeliveryData,
        } = payload;
        const isNew = document.isNew();
        const beaconFeature = toggles.isAllAccess(Toggles.KEYS.EMAIL_TRACKING);
        const emailTemplateExperiment = toggles.isAllAccess(
          Toggles.KEYS.EMAIL_TEMPLATE_EXPERIMENT
        );
        const sendDocumentPayload = this.documentDataBuilder.buildSendDocumentPayload(
          {
            document,
            settings,
            bankTransferFeatureEnabled,
            cardPaymentsFeatureEnabled,
            paypalFeatureEnabled,
            messageDeliveryData,
            emailTemplateExperiment,
            accountEmail: account.email,
            hasTrackingFeature: beaconFeature,
          }
        );

        return this.documentDataService
          .sendDocument(document.id, sendDocumentPayload)
          .pipe(
            flatMap((documentProps) => {
              const sentDocument = this.documentDataBuilder.buildEntity(
                documentProps,
                null,
                false,
                isNew
              );

              return concat([
                saveEntityStore(this.entityType, sentDocument, false),
                setDispatchedResponse(
                  this.entityType,
                  dispatchId,
                  ActionsEnum.SEND_DOCUMENT,
                  true,
                  sentDocument
                ),
              ]);
            }),
            catchError((error) =>
              of(
                setDispatchedResponse(
                  this.entityType,
                  dispatchId,
                  ActionsEnum.SEND_DOCUMENT,
                  false,
                  document,
                  error
                )
              )
            )
          );
      }
    )
  );
}
